import React, {useCallback, useEffect} from 'react';
import {TouchableOpacity, View} from 'react-native';
import CCText from '@/Atoms/CCText';
import CCTextInput from '@/Atoms/CCTextInput';
import CCColors from '@/Utils/CCColors';
import {couponStyles} from './styles';
import {CouponCodeProps} from './types';
import CouponConfirmDialog from './CouponConfirmDialog';
import {
  computeFinalPrice,
  computeSavings,
  useCouponStore,
} from '@/Store/couponStore';
import useValidateCoupon from '@/Hooks/RenewalApi/useValidateCoupon';

const TagIcon = ({
  size = 14,
  color = '#806234',
}: {
  size?: number;
  color?: string;
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true">
    <path
      d="M2 2h6l6 6-6 6-6-6V2z"
      stroke={color}
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
    <circle cx="5.5" cy="5.5" r="1.1" fill={color} />
  </svg>
);

const CheckIcon = () => (
  <CCText style={{fontSize: 14, color: CCColors.Primary.Aqua}}>✓</CCText>
);

const XIcon = () => (
  <CCText style={{fontSize: 14, color: CCColors.TextColor.Grey}}>✕</CCText>
);

function savingsLabel(discountType: string, discountValue: string): string {
  const val = parseFloat(discountValue);
  return discountType === 'percentage' ? `${val}% off` : `₹${val} off`;
}

function ErrorDisplay({
  couponError,
  couponCode,
}: {
  couponError: string;
  couponCode: string;
}) {
  if (couponCode.trim()) {
    return (
      <View style={couponStyles.errorRow}>
        <CCText style={couponStyles.errorText}>Hmm,</CCText>
        <View style={couponStyles.errorCodeChip}>
          <CCText style={couponStyles.errorCodeText}>
            {couponCode.trim()}
          </CCText>
        </View>
        <CCText style={couponStyles.errorText}>
          didn't work. Check the code or try another.
        </CCText>
      </View>
    );
  }
  return <CCText style={couponStyles.errorText}>{couponError}</CCText>;
}

/**
 * CouponCode — self-contained coupon input backed by the global `useCouponStore`.
 *
 * All coupon state lives in the global store. This component:
 * - Reads state from the store
 * - Makes the API call and updates the store directly
 * - Calls parent callbacks (`onSuccess`, `onError`, `onRemove`) so the parent
 *   can react to coupon changes (e.g. update `discountedPrice`).
 *
 * Usage:
 * ```tsx
 * <CouponCode
 *   basePrice={1000}
 *   currentEffectivePrice={systemDiscountedPrice}
 *   onSuccess={({ coupon, couponFinalPrice, couponSavings }) => {
 *     // update local discounted price display
 *   }}
 *   onError={(msg) => { setError(msg); }}
 *   onRemove={() => { setDiscountedPrice(null); }}
 *   couponExtraParams={{ tournament_id: 123 }}
 * />
 * ```
 *
 * The parent does NOT pass coupon code, applied/pending coupon, or error state —
 * all of that is owned by the component via the global store.
 */
export default function CouponCode({
  basePrice,
  currentEffectivePrice,
  discountedPrice,
  pendingDiscountedPrice,
  currentDiscountLabel,
  currencySymbol = '₹',
  disabled,
  disabledMessage,
  compact,
  couponExtraParams,
  containerStyle,
  // Callbacks
  onSuccess,
  onError,
  onRemove,
}: CouponCodeProps) {
  // ── Store state ─────────────────────────────────────────────────
  const storeCouponCode = useCouponStore(s => s.couponCode);
  const storeApplied = useCouponStore(s => s.appliedCoupon);
  const storePending = useCouponStore(s => s.pendingCoupon);
  const storeError = useCouponStore(s => s.couponError);
  const storeLoading = useCouponStore(s => s.isCouponValidating);
  const setCouponCode = useCouponStore(s => s.setCouponCode);
  const setAppliedCoupon = useCouponStore(s => s.setAppliedCoupon);
  const setPendingCoupon = useCouponStore(s => s.setPendingCoupon);
  const setCouponError = useCouponStore(s => s.setCouponError);
  const setIsCouponValidating = useCouponStore(s => s.setIsCouponValidating);

  // ── Local UI state ────────────────────────────────────────────────
  const [open, setOpen] = React.useState(false);
  const [swapMode, setSwapMode] = React.useState(false);
  const [inputFocused, setInputFocused] = React.useState(false);

  // ── Effects ────────────────────────────────────────────────────────
  useEffect(() => {
    if (storeError) {
      setOpen(true);
    }
  }, [storeError]);

  useEffect(() => {
    if (storeApplied) {
      setSwapMode(false);
    }
  }, [storeApplied]);

  // ── Computed ───────────────────────────────────────────────────────
  const savings =
    storeApplied && basePrice ? computeSavings(storeApplied, basePrice) : null;

  const applyFontSize = compact ? 12 : 13;

  // ── API call ──────────────────────────────────────────────────────
  const {mutate: validateCoupon} = useValidateCoupon(
    useCallback(
      (data: any) => {
        setIsCouponValidating(false);
        if (data.valid && data.coupon) {
          const incoming = data.coupon;
          const bp = basePrice ?? 0;
          const couponFinalPrice = computeFinalPrice(incoming, bp);
          const effectivePrice = currentEffectivePrice ?? bp;

          if (couponFinalPrice > effectivePrice) {
            // Treat as pending (worse deal than current)
            setPendingCoupon({code: incoming.code, coupon: incoming});
            return;
          }

          // Apply immediately
          setAppliedCoupon(incoming);
          setCouponCode('');
          setCouponError('');

          const savingsAmt = computeSavings(incoming, bp);
          onSuccess?.({
            coupon: incoming,
            basePrice: bp,
            couponFinalPrice,
            couponSavings: savingsAmt,
          });
        } else {
          const msg = data.error || 'Invalid coupon code';
          setCouponError(msg);
          onError?.(msg);
        }
      },
      [
        basePrice,
        currentEffectivePrice,
        onSuccess,
        onError,
        setAppliedCoupon,
        setCouponCode,
        setCouponError,
        setPendingCoupon,
        setIsCouponValidating,
      ],
    ),
    useCallback(
      (_err: any) => {
        setIsCouponValidating(false);
        const msg = 'Failed to validate coupon. Please try again.';
        setCouponError(msg);
        onError?.(msg);
      },
      [onError, setCouponError, setIsCouponValidating],
    ),
  );

  // ── Actions ────────────────────────────────────────────────────────
  const handleApply = useCallback(() => {
    const code = storeCouponCode.trim();
    if (!code || storeLoading) {
      return;
    }
    setIsCouponValidating(true);
    setCouponError('');
    validateCoupon({
      coupon_code: code,
      source: 'tournament_registration',
      ...couponExtraParams,
    });
  }, [
    storeCouponCode,
    storeLoading,
    validateCoupon,
    couponExtraParams,
    setIsCouponValidating,
    setCouponError,
  ]);

  const handleRemove = useCallback(() => {
    setCouponCode('');
    setAppliedCoupon(null);
    setPendingCoupon(null);
    setCouponError('');
    onRemove?.();
  }, [
    setCouponCode,
    setAppliedCoupon,
    setPendingCoupon,
    setCouponError,
    onRemove,
  ]);

  const handleConfirmPending = useCallback(() => {
    const p = storePending;
    if (p) {
      setAppliedCoupon(p.coupon);
      setCouponCode('');
      setPendingCoupon(null);
      const bp = basePrice ?? 0;
      const finalPrice = computeFinalPrice(p.coupon, bp);
      const savingsAmt = computeSavings(p.coupon, bp);
      onSuccess?.({
        coupon: p.coupon,
        basePrice: bp,
        couponFinalPrice: finalPrice,
        couponSavings: savingsAmt,
      });
    }
    setPendingCoupon(null);
  }, [
    storePending,
    basePrice,
    onSuccess,
    setAppliedCoupon,
    setCouponCode,
    setPendingCoupon,
  ]);

  const handleCancelPending = useCallback(() => {
    setPendingCoupon(null);
  }, [setPendingCoupon]);

  // ── Confirm dialog (shared) ───────────────────────────────────────
  const confirmDialog = (
    <CouponConfirmDialog
      basePrice={basePrice}
      pendingCoupon={storePending ?? null}
      appliedCoupon={storeApplied}
      currentDiscountLabel={currentDiscountLabel}
      currentDiscountedPrice={currentEffectivePrice ?? null}
      pendingDiscountedPrice={pendingDiscountedPrice ?? null}
      onConfirm={handleConfirmPending}
      onCancel={handleCancelPending}
    />
  );

  // ── DISABLED ──────────────────────────────────────────────────────
  if (disabled) {
    return (
      <>
        {confirmDialog}
        <View style={[couponStyles.disabledBox, containerStyle]}>
          <CCText fontSize={12} color={CCColors.TextColor.Grey}>
            {disabledMessage ?? 'Coupon codes are not applicable.'}
          </CCText>
        </View>
      </>
    );
  }

  // ── APPLIED ───────────────────────────────────────────────────────
  if (storeApplied) {
    return (
      <>
        {confirmDialog}
        <View style={containerStyle}>
          <View
            style={[
              couponStyles.appliedWrap,
              compact && couponStyles.compactAppliedWrap,
            ]}>
            <View style={couponStyles.appliedLeft}>
              <View style={couponStyles.appliedBadge}>
                <CheckIcon />
              </View>
              <View style={couponStyles.appliedTextWrap}>
                <CCText
                  style={[
                    couponStyles.appliedCode,
                    compact && couponStyles.compactAppliedCode,
                  ]}>
                  {storeApplied.code} applied
                </CCText>
                {savings !== null && (
                  <CCText
                    style={[
                      couponStyles.appliedSavings,
                      compact && couponStyles.compactAppliedSavings,
                    ]}>
                    You saved {currencySymbol}
                    {savings} ·{' '}
                    {savingsLabel(
                      storeApplied.discount_type,
                      storeApplied.discount_value,
                    )}
                  </CCText>
                )}
              </View>
            </View>
            <TouchableOpacity
              style={couponStyles.removeBtn}
              onPress={handleRemove}
              hitSlop={{top: 8, bottom: 8, left: 8, right: 8}}>
              <XIcon />
            </TouchableOpacity>
          </View>

          {!swapMode ? (
            <TouchableOpacity
              style={couponStyles.swapLink}
              onPress={() => {
                setSwapMode(true);
                setCouponCode('');
              }}>
              <TagIcon />
              <CCText style={couponStyles.swapLinkText}>
                Have another code?
              </CCText>
              <CCText style={couponStyles.swapLinkPlus}> +</CCText>
            </TouchableOpacity>
          ) : (
            <View style={couponStyles.openWrap}>
              <View style={couponStyles.inputRow}>
                <CCTextInput
                  icon={
                    <View style={{position: 'absolute', left: 10}}>
                      <TagIcon />
                    </View>
                  }
                  fontSize={applyFontSize}
                  testID="couponInput"
                  value={storeCouponCode}
                  onChangeText={setCouponCode}
                  placeholder="Enter another code"
                  onFocus={() => setInputFocused(true)}
                  onBlur={() => setInputFocused(false)}
                  style={couponStyles.couponInput}
                />
                <TouchableOpacity
                  style={[
                    couponStyles.applyBtn,
                    compact && couponStyles.compactApplyBtn,
                    (!storeCouponCode.trim() || storeLoading) &&
                      couponStyles.applyBtnDisabled,
                  ]}
                  onPress={handleApply}
                  disabled={!storeCouponCode.trim() || storeLoading}>
                  <CCText
                    fontWeight="semibold"
                    style={{fontSize: applyFontSize, color: '#fff'}}>
                    {storeLoading ? '...' : 'Apply'}
                  </CCText>
                </TouchableOpacity>
              </View>
              {!!storeError && (
                <ErrorDisplay
                  couponError={storeError}
                  couponCode={storeCouponCode}
                />
              )}
              <TouchableOpacity
                style={couponStyles.cancelLink}
                onPress={() => setSwapMode(false)}>
                <CCText style={couponStyles.cancelText}>Cancel</CCText>
              </TouchableOpacity>
            </View>
          )}

          {basePrice != null && discountedPrice != null && savings != null && (
            <View style={couponStyles.summaryBox}>
              <View style={couponStyles.summaryRow}>
                <CCText fontSize={12} color={CCColors.TextColor.Grey}>
                  Original
                </CCText>
                <CCText fontSize={12} color={CCColors.TextColor.Grey}>
                  {currencySymbol}
                  {basePrice}
                </CCText>
              </View>
              <View style={couponStyles.summaryRow}>
                <CCText fontSize={12} color={CCColors.Primary.Aqua}>
                  Coupon ({storeApplied.code})
                </CCText>
                <CCText fontSize={12} color={CCColors.Primary.Aqua}>
                  −{currencySymbol}
                  {savings}
                </CCText>
              </View>
              <View
                style={[couponStyles.summaryRow, couponStyles.summaryTotal]}>
                <CCText
                  fontSize={13}
                  fontWeight="semibold"
                  color={CCColors.TextColor.Dark}>
                  You pay
                </CCText>
                <CCText
                  fontSize={13}
                  fontWeight="semibold"
                  color={CCColors.TextColor.Dark}>
                  {currencySymbol}
                  {discountedPrice}
                </CCText>
              </View>
            </View>
          )}
        </View>
      </>
    );
  }

  // ── COLLAPSED ─────────────────────────────────────────────────────
  if (!open) {
    return (
      <>
        {confirmDialog}
        <TouchableOpacity
          style={[
            couponStyles.collapsedBtn,
            compact && couponStyles.compactCollapsedBtn,
            containerStyle,
          ]}
          onPress={() => setOpen(true)}
          activeOpacity={0.75}>
          <TagIcon />
          <CCText
            style={[
              couponStyles.collapsedText,
              compact && couponStyles.compactCollapsedText,
            ]}>
            Have a coupon code?
          </CCText>
          <CCText style={couponStyles.chevron}>+</CCText>
        </TouchableOpacity>
      </>
    );
  }

  // ── OPEN ──────────────────────────────────────────────────────────
  return (
    <>
      {confirmDialog}
      <View style={[couponStyles.openWrap, containerStyle]}>
        <View style={couponStyles.inputRow}>
          <CCTextInput
            icon={
              <View style={{position: 'absolute', left: 10}}>
                <TagIcon />
              </View>
            }
            fontSize={applyFontSize}
            testID="couponInput"
            value={storeCouponCode}
            onChangeText={setCouponCode}
            placeholder="Enter coupon code"
            onFocus={() => setInputFocused(true)}
            onBlur={() => setInputFocused(false)}
            style={couponStyles.couponInput}
          />
          <TouchableOpacity
            style={[
              couponStyles.applyBtn,
              compact && couponStyles.compactApplyBtn,
              (!storeCouponCode.trim() || storeLoading) &&
                couponStyles.applyBtnDisabled,
            ]}
            onPress={handleApply}
            disabled={!storeCouponCode.trim() || storeLoading}>
            <CCText
              fontWeight="semibold"
              style={{fontSize: applyFontSize, color: '#fff'}}>
              {storeLoading ? '...' : 'Apply'}
            </CCText>
          </TouchableOpacity>
        </View>
        {storeError ? (
          <ErrorDisplay couponError={storeError} couponCode={storeCouponCode} />
        ) : (
          <CCText style={couponStyles.hintText}>
            Have a discount code? Enter it above.
          </CCText>
        )}
      </View>
    </>
  );
}
